import { useCallback } from 'react';

import {
  type MeResponse,
  MeResponseSchema,
  type SubscriptionId,
  type SubscriptionSettingsResponse,
  SubscriptionSettingsResponseSchema,
} from '../../api';
import { useApiClient } from '../../lib/api/api-context';
import { type ApiQueryResult, useApiQuery } from '../../lib/api/use-api-query';

/** Path of the settings routes of one subscription (C1 `settings.ts`). */
export function settingsPath(subscriptionId: SubscriptionId): string {
  return `/subscriptions/${encodeURIComponent(subscriptionId)}/settings`;
}

/** What E7 reads before it renders the form. */
export interface SettingsScreenData {
  readonly settings: SubscriptionSettingsResponse;
  /**
   * `GET /me` gives what the settings response lacks today: the channel name, the account
   * currency, the account's daily limit and the cause of a suspension. Review decision D3 asks
   * C1 to add them to `GET …/settings`; this second read goes away then.
   */
  readonly me: MeResponse;
}

/** Loads `GET …/settings` and `GET /me` together: E7 shows its form only when both arrived. */
export function useSettingsData(
  subscriptionId: SubscriptionId,
): ApiQueryResult<SettingsScreenData> {
  const api = useApiClient();
  const load = useCallback(
    async (signal: AbortSignal): Promise<SettingsScreenData> => {
      const [settings, me] = await Promise.all([
        api.get(settingsPath(subscriptionId), SubscriptionSettingsResponseSchema, signal),
        api.get('/me', MeResponseSchema, signal),
      ]);
      return { settings, me };
    },
    [api, subscriptionId],
  );
  return useApiQuery(load);
}
